import { BEIGE_500, BEIGE_600, GRAY_400, GRAY_700, GRAY_800, GRAY_900 } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'

import { AssignmentDocumentStatusHistoryDTO } from 'models/assignment'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { ISODateString } from 'models/misc'
import { MUIDivider } from 'components/common/MUIDivider'
import React from 'react'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

/** @interface Props for the ClientGalleryDocumentTimeline component. */
interface Props {
  /** Document status history data to display */
  documentStatusHistoryData?: AssignmentDocumentStatusHistoryDTO[]
  /** Estimated delivery date for the assignment documents */
  assignmentDocumentsEstimatedDelivery?: ISODateString
}

/**
 * ClientGalleryDocumentTimeline component displays a status history of documents in the client gallery.
 *
 * @example <ClientGalleryDocumentTimeline documentStatusHistoryData={[]} />
 */
export const ClientGalleryDocumentTimeline: React.FC<Props> = ({
  documentStatusHistoryData,
  assignmentDocumentsEstimatedDelivery
}) => {

  const { t } = useTranslation(['document_status_history'])

  return (
    <SectionedBorderBox
      title={
        <Typography color={GRAY_900} variant="text-lg" fontWeight={600}>
          {t('title')}
        </Typography>
      }
      minWidth="26rem"
      flexShrink={0}
    >
      <Stack gap={2}>

        {assignmentDocumentsEstimatedDelivery &&
          <BorderBoxWrapper
            padding={1}
            borderColor={BEIGE_600}
            elevation="none"
            backgroundColor={BEIGE_500}
          >
            <Stack gap={0.5}>

              <Typography variant="text-sm" color={GRAY_700}>
                {t('estimated_delivery')}
              </Typography>

              <Stack gap=".4rem" flexDirection="row" alignItems="center">

                <CalendarMonthIcon sx={{ color: GRAY_900, fontSize: '1.6rem' }} />

                <Typography variant="text-sm" color={GRAY_900}>
                  {moment(assignmentDocumentsEstimatedDelivery).format('DD/MM/YYYY')}
                </Typography>

              </Stack>

            </Stack>
          </BorderBoxWrapper>
        }

        {documentStatusHistoryData && documentStatusHistoryData.map((status, index) => (
          <Stack key={status.docestateStatusId} flexDirection="row" gap="1.4rem">

            <Stack alignItems="center" gap=".4rem" paddingTop="0.4rem">

              <FiberManualRecordIcon sx={{ color: GRAY_800, fontSize: '.8rem' }} />

              {index !== documentStatusHistoryData.length - 1 && <MUIDivider orientation="vertical" sx={{ borderColor: GRAY_400, borderRightWidth: 'initial' }} />}

            </Stack>

            <Stack gap=".4rem">

              <Typography variant="text-xs" color={GRAY_700}>
                {new Date(status.createdAt).toLocaleDateString()}
              </Typography>

              <Stack flexDirection="row" alignItems="center" flexWrap="wrap" gap="0.4rem">
                <Typography variant="text-sm" fontWeight={500} color={GRAY_700}>
                  {t(`status_types.${status.status}`)}
                </Typography>
              </Stack>

            </Stack>
          </Stack>
        ))}

      </Stack>

    </SectionedBorderBox>
  )
}
